import type { RefObject } from 'react';

function isVerticallyScrollable(el: HTMLElement): boolean {
  const { overflowY } = window.getComputedStyle(el);
  const allowsScroll =
    overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay';
  return allowsScroll && el.scrollHeight > el.clientHeight + 1;
}

/** Nearest ancestor that actually scrolls vertically (panel, app shell, or document). */
function findVerticalScrollParent(start: HTMLElement): HTMLElement | null {
  let node: HTMLElement | null = start.parentElement;
  while (node) {
    if (isVerticallyScrollable(node)) {
      return node;
    }
    node = node.parentElement;
  }
  return null;
}

function scrollParentToTarget(
  parent: HTMLElement,
  target: HTMLElement,
  paddingPx: number,
): void {
  const parentRect = parent.getBoundingClientRect();
  const targetRect = target.getBoundingClientRect();
  const nextTop = targetRect.top - parentRect.top + parent.scrollTop - paddingPx;
  parent.scrollTo({ top: Math.max(0, nextTop), behavior: 'auto' });
}

function targetNeedsViewportScroll(target: HTMLElement, paddingPx: number): boolean {
  const rect = target.getBoundingClientRect();
  return rect.top < paddingPx || rect.bottom > window.innerHeight - paddingPx;
}

/**
 * Scroll so `target` is near the top of its scroll container, then move keyboard/
 * accessibility focus to it (cellphone + PC).
 */
export function scrollPanelToElement(
  panel: HTMLElement | null | undefined,
  target: HTMLElement | null | undefined,
  paddingPx = 8,
): boolean {
  if (!target) {
    return false;
  }

  const scrollParent =
    panel && isVerticallyScrollable(panel)
      ? panel
      : findVerticalScrollParent(target) ?? (panel ?? null);

  if (scrollParent && isVerticallyScrollable(scrollParent)) {
    scrollParentToTarget(scrollParent, target, paddingPx);
  }

  // Window / outer shell may still hide the target after an inner panel scroll.
  if (targetNeedsViewportScroll(target, paddingPx)) {
    target.scrollIntoView({ block: 'start', behavior: 'auto' });
  }

  if (typeof target.focus === 'function') {
    try {
      target.focus({ preventScroll: true });
    } catch {
      target.focus();
    }
  }

  return true;
}

export function scheduleScrollPanelToTop(
  panelRef: RefObject<HTMLElement | null>,
): () => void {
  let cancelled = false;
  let attempts = 0;
  const maxAttempts = 16;
  const tryScroll = () => {
    if (cancelled) {
      return;
    }
    const panel = panelRef.current;
    if (panel) {
      panel.scrollTo({ top: 0, behavior: 'auto' });
      return;
    }
    if (attempts++ < maxAttempts) {
      requestAnimationFrame(tryScroll);
    }
  };
  requestAnimationFrame(() => requestAnimationFrame(tryScroll));
  const retryTimer = window.setTimeout(tryScroll, 120);
  return () => {
    cancelled = true;
    window.clearTimeout(retryTimer);
  };
}

/**
 * Retry scroll+focus until the target mounts and layout settles (e.g. asset create
 * clears images and shrinks the form after the success banner appears).
 */
export function scheduleScrollPanelToElement(
  panelRef: RefObject<HTMLElement | null>,
  targetRef: RefObject<HTMLElement | null>,
  paddingPx = 8,
): () => void {
  let cancelled = false;
  let attempts = 0;
  const maxAttempts = 20;
  const tryScroll = () => {
    if (cancelled) {
      return;
    }
    scrollPanelToElement(panelRef.current, targetRef.current, paddingPx);
    if (attempts++ < maxAttempts && (!targetRef.current || document.activeElement !== targetRef.current)) {
      requestAnimationFrame(tryScroll);
    }
  };
  requestAnimationFrame(() => requestAnimationFrame(tryScroll));
  const timers = [80, 200, 400, 700].map((ms) => window.setTimeout(tryScroll, ms));
  return () => {
    cancelled = true;
    for (const id of timers) {
      window.clearTimeout(id);
    }
  };
}
